import React, { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { useAuthContext } from "../../../AuthContextWrapper";
import { Estate } from "../../../models/Estate";
import { OUTCOMES } from "../../../constants";
import { CheckOption, MultiCheckForm } from "../shared/MultiCheckForm";
import { enumToStringDictionary } from "@theatticusapp/atticus-shared-components";

export enum Outcomes {
  probateForms = "I need help finding probate forms",
  speakWithExport = "I would like to speak to an expert",
  findAttorney = "I need to find an attorney",
  inventoryReports = "I need to create a inventory of assets and/or reports",
  somethingElse = "Something else",
}

const somethingElseFollowUpLabel = "Let us know what else";

export const HelpWithScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow, activeEstate, setActiveEstate } = useAuthContext()
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);

  const handleComplete = async (
    checked: string[],
    checkedFollowUps: string[]
  ): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    // const estate = activeEstate ? Estate.from(activeEstate) : new Estate();
    const estate = activeEstate || new Estate();
    estate.needHelpWith = checked;

    // if somethingElse checked, get the somethingElse text entry
    let somethingElseIndex;
    const somethingElseKey = "somethingElse";
    if (checked.includes(somethingElseKey)) {
      // which checkedFollowUps index will have the text?
      helpOptions.forEach((checkOption: CheckOption, index: number) => {
        if (checkOption.key === somethingElseKey) {
          somethingElseIndex = index;
        }
      });
      if (somethingElseIndex) {
        estate.needHelpWithSomeThingElse = checkedFollowUps[somethingElseIndex];
      }
    } else {
      estate.needHelpWithSomeThingElse = "";
    }

    try {
      await estate.saveEstate();
      setActiveEstate(estate);
    } catch (error) {
      throw new Error("could not save data");
    }

    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    return true;
  };

  const outcomesDictionary = enumToStringDictionary(Outcomes);
  const helpOptions: CheckOption[] = [];
  Object.keys(outcomesDictionary).forEach((key: string) => {
    const value = outcomesDictionary[key];
    helpOptions.push({
      key,
      value,
      followUp:
        value === Outcomes.somethingElse
          ? somethingElseFollowUpLabel
          : undefined,
    });
  });
  const initFollowups = helpOptions.map((checkOption: CheckOption) =>
    activeEstate?.needHelpWithSomeThingElse &&
    checkOption.value === Outcomes.somethingElse
      ? activeEstate.needHelpWithSomeThingElse
      : ""
  );

  return currentScreen ? (
    <FlowScreenWrapper
      title="What do you need help with?"
      subTitle="Select any of the following that apply:"
    >
      <MultiCheckForm
        initChecked={activeEstate?.needHelpWith || undefined}
        initFollowUps={initFollowups}
        options={helpOptions}
        onComplete={handleComplete}
        onBack={() => navigate(-1)}
        ctaText="Get started"
      />
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
